import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { AppLayout } from '../components/layout/AppLayout';
import { Loader } from '../components/common/Loader';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../services/apiClient';
import { useNavigate, useParams } from 'react-router-dom';

interface Pet {
  id: string;
  name: string;
  species?: string;
  breed?: string;
}

interface Recommendation {
  id: string;
  date?: string;
  recomendation: string;
  doctorName?: string;
}

// Страница списка питомцев
export const RecommendationsPage: React.FC = () => {
  const { isMatched } = useAuth();
  const navigate = useNavigate();

  const { isLoading, error, data } = useApi<{ pets: Pet[] }>(
    () => apiClient.get('/webapp/recommendations/pets'),
    { autoExecute: isMatched }
  );

  if (!isMatched) {
    return (
      <AppLayout title="Рекомендации">
        <Alert severity="info" sx={{ marginTop: 2 }}>
          Для просмотра рекомендаций необходимо сопоставить карту пациента.
          Перейдите в{' '}
          <strong onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
            Мой кабинет
          </strong>
          .
        </Alert>
      </AppLayout>
    );
  }

  if (isLoading) {
    return (
      <AppLayout title="Рекомендации">
        <Loader message="Загрузка..." />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Рекомендации">
        <Paper sx={{ padding: 2, backgroundColor: '#ffebee' }}>
          <Typography color="error">Ошибка: {error}</Typography>
        </Paper>
      </AppLayout>
    );
  }

  const pets = data?.pets || [];

  return (
    <AppLayout title="Рекомендации к лечению">
      <Box>
        {pets.length === 0 ? (
          <Paper sx={{ padding: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">Данные о питомцах не найдены</Typography>
          </Paper>
        ) : (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ marginBottom: 2 }}>
              Выберите питомца для просмотра рекомендаций
            </Typography>
            <List>
              {pets.map((pet) => (
                <ListItemButton
                  key={pet.id}
                  component={Paper}
                  sx={{ marginBottom: 1, padding: 2 }}
                  onClick={() => navigate(`/recommendations/${pet.id}`)}
                >
                  <ListItemText
                    primary={pet.name}
                    secondary={[pet.species, pet.breed].filter(Boolean).join(' · ')}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </AppLayout>
  );
};

// Страница рекомендаций конкретного питомца
export const PetRecommendationsPage: React.FC = () => {
  const { petId } = useParams<{ petId: string }>();

  const { isLoading, error, data } = useApi<{ recommendations: Recommendation[] }>(
    () => apiClient.get(`/webapp/recommendations/pets/${petId}`),
    { autoExecute: !!petId }
  );

  if (isLoading) {
    return (
      <AppLayout title="Рекомендации">
        <Loader message="Загрузка..." />
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout title="Рекомендации">
        <Paper sx={{ padding: 2, backgroundColor: '#ffebee' }}>
          <Typography color="error">Ошибка: {error}</Typography>
        </Paper>
      </AppLayout>
    );
  }

  const recommendations = data?.recommendations || [];

  return (
    <AppLayout title="Рекомендации к лечению">
      <Box>
        {recommendations.length === 0 ? (
          <Paper sx={{ padding: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">Рекомендаций не найдено</Typography>
          </Paper>
        ) : (
          <Box>
            {recommendations.map((rec, idx) => (
              <Accordion key={rec.id || idx}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">
                    {rec.date
                      ? new Date(rec.date).toLocaleDateString('ru-RU')
                      : `Рекомендация ${idx + 1}`}
                    {rec.doctorName && (
                      <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                        {rec.doctorName}
                      </Typography>
                    )}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {rec.recomendation}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
    </AppLayout>
  );
};

